import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useOutlet } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Boxes,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  Eye,
  Home,
  LayoutDashboard,
  LineChart,
  LogOut,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Stamp,
  Truck,
  Users,
  Wallet,
  Wrench,
} from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useIsReadOnly, useSession } from '@/shared/auth/SessionProvider';
import { canSeeMenu, useMenuPermissions } from '@/shared/auth/useMenuPermissions';
import type { MenuKey } from '@/shared/auth/menuKeys';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { FornitaliaLogo } from '@/shared/components/brand/FornitaliaLogo';
import { HelpButton } from '@/shared/help/HelpButton';
import { cn } from '@/shared/lib/cn';
import { NuevaVersionDialog } from '@/shared/version/NuevaVersionDialog';
import { usePendingAprobacionesCount } from '@/features/aprobaciones/queries';
import { PageTransition } from './PageTransition';
import { CountrySwitcher } from './CountrySwitcher';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { FlagIcon } from '@/shared/countries/FlagIcon';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * DB-driven visibility key. Each item maps to one row-set in Supabase
   * `menu_permissions` (perfil × menu_key); `useMenuPermissions` is the
   * lookup. Items without a menuKey are visible to every authenticated
   * user — currently unused, reserved for future open-to-all entries.
   * Cold-start / fetch-failure fallback lives in
   * `MENU_PERMISSIONS_FALLBACK` (menuKeys.ts) and must mirror current
   * production state.
   */
  menuKey?: MenuKey;
  badge?: number;
  /** When true, render as read-only (muted text, no link, "Próximamente" tag). */
  disabled?: boolean;
  /**
   * Extra pathnames that should also light this item up. Used when a
   * child route lives at a different top-level path (Recepción is
   * `/recepcion` but conceptually part of Inventario, so we want the
   * Inventario nav highlighted while inside it). Matches on equality
   * or as a path prefix (`/foo/bar` highlights when on `/foo/bar/baz`).
   */
  activePaths?: string[];
};

/**
 * Nav order matches the legacy PowerApps sidebar. Per-role visibility
 * is data-driven from the `menu_permissions` Supabase table — edit a
 * row via Studio (or the future Configuración editor) to flip a module
 * on/off for a perfil. Cold-start fallback + canonical menu_key list
 * live in `@/shared/auth/menuKeys`.
 *
 * Defense-in-depth: ProtectedRoute keeps its hardcoded `allowedRoles`
 * as the upper bound; a DB row can revoke but not grant beyond what
 * code permits. Sub-routes and per-action gates (Nova flow restrictions
 * inside Ventas / Administración, visitante read-only writes, etc.)
 * stay hardcoded — those encode flow nuance a single menu_key can't.
 *
 * Adding a new module: new menu_key in `MENU_KEYS`, fallback row in
 * `MENU_PERMISSIONS_FALLBACK`, INSERT into `menu_permissions` (one per
 * perfil), ProtectedRoute `allowedRoles` on the route, and the entry
 * here. Login lands on `/` by default — if a future role excludes
 * Inicio (mantenimiento + nova today), update `defaultLandingForProfile`.
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Inicio', icon: Home, menuKey: 'home' },
  { to: '/contactos', label: 'Contactos', icon: Users, menuKey: 'contactos' },
  { to: '/ventas', label: 'Ventas', icon: ShoppingCart, menuKey: 'ventas' },
  { to: '/compras', label: 'Compras', icon: ShoppingBag, menuKey: 'compras' },
  { to: '/recepcion', label: 'Recepciones', icon: Truck, menuKey: 'recepcion' },
  { to: '/inventario', label: 'Inventario', icon: Boxes, menuKey: 'inventario' },
  { to: '/mantenimiento', label: 'Mantenimiento', icon: Wrench, menuKey: 'mantenimiento' },
  { to: '/administracion', label: 'Administración', icon: ClipboardList, menuKey: 'administracion' },
  { to: '/configuracion', label: 'Configuración', icon: SlidersHorizontal, menuKey: 'configuracion' },
  { to: '/aprobaciones', label: 'Aprobaciones', icon: Stamp, menuKey: 'aprobaciones' },
  // Facturación se movió adentro de Reporting (2026-07-21) — ya no tiene
  // entrada propia en el sidebar ni ruta.
  { to: '/reporting', label: 'Reporting', icon: LineChart, menuKey: 'reportes' },
  { to: '/tesoreria', label: 'Tesorería', icon: Wallet, menuKey: 'tesoreria' },
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, menuKey: 'dashboard' },
  // Chatbot is temporarily hidden — the legacy implementation is
  // deprecated and we'll revisit when a replacement lands. Re-add when
  // ready: `{ to: '/chatbot', label: 'Chatbot', icon: Bot },`.
];

const SIDEBAR_COLLAPSED_KEY = 'fornitalia.sidebarCollapsed';

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    // No stored preference → default to collapsed on mobile (the 68px
    // rail leaves room for content) and expanded on desktop. Respects
    // any subsequent toggle since `useEffect` writes the choice back to
    // localStorage on change.
    return window.matchMedia('(max-width: 767px)').matches;
  } catch {
    return false;
  }
}

function readIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(max-width: 767px)').matches;
  } catch {
    return false;
  }
}

export function AppShell() {
  const { profile, signOut } = useSession();
  const { config: activeCountry } = useActiveCountry();
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();
  const reduce = useReducedMotion();
  const aprobacionesBadge = usePendingAprobacionesCount();
  const isReadOnly = useIsReadOnly();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [isMobile, setIsMobile] = useState(readIsMobile);

  // Persist the user's preference so a page reload doesn't reset to
  // expanded. Falls back silently if localStorage isn't available.
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // Keep `isMobile` in sync with viewport size so the sidebar locks to
  // its collapsed rail below the md breakpoint and re-expands (using
  // the user's stored preference) on desktop.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // On mobile we always show the icon-only rail — no toggle, no
  // expanded variant. Desktop honours the stored preference.
  const effectiveCollapsed = isMobile || collapsed;

  const { visibility } = useMenuPermissions();
  const visible = NAV_ITEMS.filter(
    (item) => !item.menuKey || canSeeMenu(profile, visibility, item.menuKey),
  );
  const displayName =
    `${profile?.nombre ?? ''} ${profile?.apellido ?? ''}`.trim() || profile?.usuario_app || '';

  const shell = (
    <div className="flex h-dvh bg-muted/40">
      <aside
        className={cn(
          'flex shrink-0 flex-col bg-card shadow-sm transition-[width] duration-200 ease-out',
          effectiveCollapsed ? 'w-[68px]' : 'w-60',
        )}
      >
        <div
          className={cn(
            'flex items-center py-5 transition-[padding] duration-200',
            effectiveCollapsed ? 'justify-center px-2' : 'justify-between gap-2 px-6',
          )}
        >
          {effectiveCollapsed ? (
            <FornitaliaLogo variant="icon" size="sm" />
          ) : (
            <FornitaliaLogo variant="horizontal" size="sm" />
          )}
        </div>
        <div className="mx-3 border-b" />

        <nav
          className={cn(
            'nav-list flex-1 space-y-1 overflow-y-auto p-3',
            effectiveCollapsed && 'px-2',
          )}
        >
          {visible.map((item) => {
            if (item.disabled) {
              return (
                <div
                  key={item.to}
                  className={cn(
                    'flex cursor-not-allowed items-center rounded-lg text-sm font-medium text-muted-foreground/60',
                    effectiveCollapsed
                      ? 'h-10 justify-center px-0'
                      : 'gap-3 px-3 py-2.5',
                  )}
                  title={effectiveCollapsed ? `${item.label} · Próximamente` : 'Próximamente'}
                  aria-disabled="true"
                >
                  <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
                  {!effectiveCollapsed && (
                    <>
                      <span className="truncate">{item.label}</span>
                      <span className="ml-auto rounded-full border border-border bg-muted/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Próximamente
                      </span>
                    </>
                  )}
                </div>
              );
            }
            const badge = item.to === '/aprobaciones' ? aprobacionesBadge : item.badge;
            // `activePaths` opt-in for cross-route highlighting. We
            // pre-compute the match here so the className callback
            // stays a pure function of `isActive`.
            const aliasActive = item.activePaths?.some(
              (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
            );
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                title={effectiveCollapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'relative flex items-center rounded-lg text-sm font-medium text-muted-foreground transition-colors',
                    'hover:bg-primary hover:text-primary-foreground',
                    effectiveCollapsed
                      ? 'h-10 justify-center px-0'
                      : 'gap-3 px-3 py-2.5',
                    // On the icon-only rail, a full bg-primary square
                    // bleeds visually next to page content. Use a
                    // subtler indicator (tinted icon + soft tint) so the
                    // active state still reads as active without
                    // looking like a red blob beside the cards.
                    (isActive || aliasActive) &&
                      (effectiveCollapsed
                        ? 'bg-primary/10 text-primary'
                        : 'bg-primary text-primary-foreground'),
                  )
                }
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
                {!effectiveCollapsed && <span className="truncate">{item.label}</span>}
                {badge ? (
                  effectiveCollapsed ? (
                    <span
                      className="absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-destructive-foreground ring-2 ring-card"
                      aria-label={`${badge} pendientes`}
                    >
                      {badge > 9 ? '9+' : badge}
                    </span>
                  ) : (
                    <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-destructive-foreground ring-2 ring-card">
                      {badge}
                    </span>
                  )
                ) : null}
              </NavLink>
            );
          })}
        </nav>

        <div className="mx-3 border-b" />
        <div className={cn('p-3', !effectiveCollapsed && 'p-4')}>
          {/* Ayuda por módulo — arriba del usuario. Se monta una sola vez y
              resuelve su contenido según la ruta activa (shared/help). */}
          <HelpButton collapsed={effectiveCollapsed} />
          {effectiveCollapsed ? (
            // Collapsed sidebar — flag-only button (interactive switcher
            // for cross-country users) or static badge (everyone else).
            <>
              <CountrySwitcher collapsed />
              <CollapsedFlagBadge />
            </>
          ) : (
            displayName && (
              <p
                className="mb-2 flex items-center gap-1.5 truncate px-3 text-xs text-muted-foreground"
                title={`${displayName} · ${activeCountry.name}`}
              >
                <FlagIcon code={activeCountry.code} title={activeCountry.name} />
                <span className="min-w-0 flex-1 truncate">{displayName}</span>
                <CountrySwitcher />
              </p>
            )
          )}
          <Button
            variant="ghost"
            size={effectiveCollapsed ? 'icon' : 'sm'}
            title={effectiveCollapsed ? 'Cerrar sesión' : undefined}
            className={cn(
              'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
              effectiveCollapsed
                ? 'mx-auto h-10 w-10'
                : 'w-full justify-start gap-2',
            )}
            onClick={() => setConfirmLogout(true)}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {!effectiveCollapsed && 'Cerrar sesión'}
          </Button>
          {!isMobile && (
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              title={collapsed ? 'Expandir menú' : 'Contraer menú'}
              aria-label={collapsed ? 'Expandir menú' : 'Contraer menú'}
              className={cn(
                'mt-2 inline-flex items-center gap-2 rounded-md border text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
                collapsed
                  ? 'mx-auto h-9 w-10 justify-center'
                  : 'w-full justify-center px-3 py-1.5',
              )}
            >
              {collapsed ? (
                <ChevronsRight className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <>
                  <ChevronsLeft className="h-3.5 w-3.5" aria-hidden />
                  <span>Contraer</span>
                </>
              )}
            </button>
          )}
        </div>
      </aside>

      <main className="relative flex flex-1 flex-col overflow-hidden">
        {/* Modo Lectura banner — visitante is universally read-only.
            Surfaces their state up-top so they don't think the buttons
            are broken when the API rejects writes (the backend returns
            403 read_only_role on every non-GET request). */}
        {isReadOnly && profile?.perfil === 'visitante' && (
          <div className="flex shrink-0 items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-900 sm:text-sm">
            <Eye className="h-4 w-4 shrink-0" aria-hidden />
            <span>Modo lectura — el rol Visitante puede ver toda la app pero no modificar ni crear registros.</span>
          </div>
        )}
        <div className="relative flex-1 overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <PageTransition key={location.pathname} className="flex h-full flex-col p-3 sm:p-6 md:p-8">
              {outlet ?? <Outlet />}
            </PageTransition>
          </AnimatePresence>
        </div>
      </main>

      <NuevaVersionDialog />

      <Dialog open={confirmLogout} onOpenChange={setConfirmLogout}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cerrar sesión</DialogTitle>
            <DialogDescription>
              ¿Seguro que querés cerrar sesión? Vas a volver a la pantalla de inicio de sesión.
            </DialogDescription>
          </DialogHeader>
          <DialogBody />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmLogout(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmLogout(false);
                signOut();
                navigate('/login', { replace: true });
              }}
            >
              Cerrar sesión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (reduce) return shell;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="contents"
    >
      {shell}
    </motion.div>
  );
}

/**
 * Static flag indicator shown in the collapsed sidebar. Hides itself when
 * the user can switch countries (CountrySwitcher renders its own button in
 * that case, so we'd otherwise double up).
 */
function CollapsedFlagBadge() {
  const { config, canSwitch } = useActiveCountry();
  if (canSwitch) return null;
  return (
    <div
      className="mx-auto mb-2 flex h-8 w-10 items-center justify-center rounded-md border bg-card"
      title={`País: ${config.name}`}
      aria-label={`País: ${config.name}`}
    >
      <FlagIcon code={config.code} title={config.name} size={14} />
    </div>
  );
}
